/**
 * Portrait-pool picker (L13/C15).
 *
 * Loads the fixed 100-entry portrait pool authored by `tools/generatePortraits.ts`
 * and provides the lookup helpers the character creator uses. Replaces the
 * per-player LLM portrait flow — players now pick from the prebuilt set.
 *
 * The manifest lives under `public/engine/portraits/pool/manifest.json` and is served
 * statically by Vite. The loader is injectable so tests and Node harnesses
 * can stub it without a real `fetch`.
 */

export type PoolRace = "human" | "elf" | "dwarf" | "halfling" | "half-orc";
export type PoolGender = "male" | "female";

export interface PortraitPoolEntry {
  readonly id: string;
  readonly race: PoolRace;
  readonly gender: PoolGender;
  readonly classAffinities: readonly string[];
  readonly path: string;
}

export interface PortraitPoolManifest {
  readonly generator?: string;
  readonly entries: readonly PortraitPoolEntry[];
}

export const PORTRAIT_POOL_MANIFEST_URL = "/engine/portraits/pool/manifest.json";

export type ManifestFetcher = (url: string) => Promise<PortraitPoolManifest>;

/**
 * Full D&D race id → pool race. Subrace variants (high-elf, hill-dwarf,
 * lightfoot-halfling) collapse to their base race. Unmapped races
 * (tiefling, dragonborn) have no pool coverage and will produce an empty
 * result — the UI should surface this.
 */
const RACE_TO_POOL: Readonly<Record<string, PoolRace>> = {
  human: "human",
  elf: "elf",
  "high-elf": "elf",
  "wood-elf": "elf",
  dwarf: "dwarf",
  "hill-dwarf": "dwarf",
  "mountain-dwarf": "dwarf",
  halfling: "halfling",
  "lightfoot-halfling": "halfling",
  "stout-halfling": "halfling",
  "half-orc": "half-orc",
};

export function mapRaceToPool(raceId: string): PoolRace | null {
  return RACE_TO_POOL[raceId.trim().toLowerCase()] ?? null;
}

const defaultFetcher: ManifestFetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`portrait manifest fetch failed: ${res.status}`);
  return (await res.json()) as PortraitPoolManifest;
};

let cached: readonly PortraitPoolEntry[] | null = null;

export async function loadPortraitPool(
  fetcher: ManifestFetcher = defaultFetcher,
  url: string = PORTRAIT_POOL_MANIFEST_URL,
): Promise<readonly PortraitPoolEntry[]> {
  if (cached) return cached;
  const manifest = await fetcher(url);
  cached = manifest.entries;
  return cached;
}

/** Reset the in-memory cache — tests only. */
export function resetPortraitPoolCache(): void {
  cached = null;
}

export interface PoolFilter {
  readonly race?: PoolRace | null;
  readonly classAffinity?: string | null;
  readonly gender?: PoolGender | null;
}

export function filterPool(
  entries: readonly PortraitPoolEntry[],
  filter: PoolFilter,
): readonly PortraitPoolEntry[] {
  return entries.filter((e) => {
    if (filter.race && e.race !== filter.race) return false;
    if (filter.gender && e.gender !== filter.gender) return false;
    if (filter.classAffinity) {
      const cls = filter.classAffinity.trim().toLowerCase();
      if (!e.classAffinities.some((c) => c.toLowerCase() === cls)) return false;
    }
    return true;
  });
}

/**
 * Pick a deterministic default portrait for a race/class pair. Used to
 * seed the picker with a sensible choice the player can keep or change.
 * Returns the first entry that matches race + class affinity; if none
 * match, widens to race-only; if still none, returns null.
 */
export function pickDefaultPortrait(
  entries: readonly PortraitPoolEntry[],
  request: { readonly race: string; readonly characterClass: string },
): PortraitPoolEntry | null {
  const pool = mapRaceToPool(request.race);
  if (!pool) return null;
  const exact = filterPool(entries, { race: pool, classAffinity: request.characterClass });
  if (exact.length > 0) return exact[0] ?? null;
  const byRace = filterPool(entries, { race: pool });
  return byRace[0] ?? null;
}
